import * as Yup from "yup";
import { postcodeValidationRegex } from "cpl-react-common/components/Constants";
import { CountryOption, findCountryByIsoCode } from "../../selectComponents/countrySelect/countryOptions";
import { MAX_ADDRESS_LINES, UK_COUNTRY_ISO_CODE } from "../utils/addressUtils";

export interface AddressFormValues {
  addressLines: string[];
  city: string;
  county: string;
  countryCodeID?: number;
  countryIsoCode?: string;
  postCode: string;
}

export const emptyAddressFormValues: AddressFormValues = {
  addressLines: Array(MAX_ADDRESS_LINES).fill(""),
  city: "",
  county: "",
  countryCodeID: undefined,
  countryIsoCode: undefined,
  postCode: "",
};

export const addressWithCountry = (address: AddressFormValues, country?: CountryOption): AddressFormValues =>
  country
    ? { ...address, countryCodeID: country.countryCodeID, countryIsoCode: country.isO3166_1_2LetterCode }
    : address;

export const addressWithCountryCodeID = (address: AddressFormValues, countries: CountryOption[]): AddressFormValues =>
  address.countryCodeID !== undefined || !address.countryIsoCode
    ? address
    : addressWithCountry(address, findCountryByIsoCode(countries, address.countryIsoCode));

export const addressSchema = Yup.object({
  addressLines: Yup.array()
    .of(Yup.string())
    .test("address-line-1-required", "Address line 1 is required", function (lines) {
      if (lines?.[0]?.trim()) {
        return true;
      }
      return this.createError({ path: `${this.path}[0]`, message: "Address line 1 is required" });
    }),
  city: Yup.string(),
  county: Yup.string(),
  countryCodeID: Yup.number().test("country-required", "Country is required", function (countryCodeID) {
    return countryCodeID !== undefined || !!this.parent.countryIsoCode;
  }),
  countryIsoCode: Yup.string(),
  postCode: Yup.string()
    .test("postcode-required", "Postcode is required", (postCode) => !!postCode?.trim())
    .when("countryIsoCode", {
      is: UK_COUNTRY_ISO_CODE,
      then: (schema) =>
        schema.test(
          "uk-postcode-format",
          "Postcode is not a valid UK postcode (e.g. BS8 4QT)",
          (postCode) => !postCode?.trim() || postcodeValidationRegex.test(postCode.trim())
        ),
    }),
});
